'use client';

import { Navbar } from '@/components/navigation/Navbar';
import { Footer } from '@/components/navigation/Footer';
import { Section } from '@/components/ui/Section';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Mail, Info } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { isValidEmail } from '@/lib/security';

export default function MotDePasseOubliePage() {
  const t = useTranslations('auth.forgotPassword');
  const tLogin = useTranslations('auth.login');
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/csrf')
      .then((res) => res.json())
      .then((data) => setCsrfToken(data.token))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!email.trim()) {
      setErrors({ email: t('errorEmail') });
      return;
    }
    if (!isValidEmail(email.trim())) {
      setErrors({ email: t('errorEmailInvalid') });
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setErrors({ general: 'Trop de requêtes. Veuillez réessayer plus tard.' });
        } else if (response.status === 403) {
          setErrors({ general: 'Erreur de sécurité. Veuillez rafraîchir la page.' });
          fetch('/api/csrf').then((res) => res.json()).then((d) => setCsrfToken(d.token));
        } else {
          setErrors({ general: data.error || 'Erreur lors de l\'envoi du code' });
        }
        setStatus('error');
        return;
      }

      setStatus('success');
      router.push(`/connexion/verifier-code?email=${encodeURIComponent(email.trim().toLowerCase())}`);
    } catch {
      setErrors({ general: 'Une erreur est survenue. Veuillez réessayer.' });
      setStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main>
        <Section variant="gradient" className="pt-32 pb-20">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-7xl font-bold leading-normal mb-6 bg-gradient-to-r from-accent to-secondary bg-clip-text text-transparent">
              {t('title')}
            </h1>
            <p className="text-2xl text-foreground max-w-2xl mx-auto">{t('description')}</p>
          </div>

          <div className="max-w-md mx-auto">
            <Card className="bg-white/90">
              <form onSubmit={handleSubmit} noValidate className="space-y-6">
                {errors.general && (
                  <div className="p-3 bg-danger/20 border border-danger/40 rounded-lg">
                    <p className="text-danger text-sm">{errors.general}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-foreground mb-2">
                    {tLogin('email')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-accent/70" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) setErrors({ ...errors, email: '' });
                      }}
                      className={`w-full pl-11 pr-4 py-3 bg-white border rounded-lg text-foreground focus:outline-none transition-colors ${
                        errors.email ? 'border-danger' : 'border-accent/30 focus:border-accent'
                      }`}
                      required
                      maxLength={255}
                      autoComplete="email"
                      disabled={status === 'loading'}
                    />
                  </div>
                  {errors.email && <p className="text-danger text-sm mt-1">{errors.email}</p>}
                </div>

                <div className="flex gap-3 p-4 bg-accent/10 border border-accent/30 rounded-lg">
                  <Info className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground">{t('info')}</p>
                </div>

                <Button type="submit" size="lg" className="w-full" disabled={status === 'loading'}>
                  {status === 'loading' ? t('loading') : t('submit')}
                </Button>

                <div className="text-center">
                  <Link
                    href="/connexion"
                    className="text-accent hover:text-secondary text-sm font-medium transition-colors"
                  >
                    {t('backToLogin')}
                  </Link>
                </div>
              </form>
            </Card>
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
